import { useEffect, useState, useContext } from "react";
import { Input, Modal } from "antd";
import {
  getMergeableCaseList,
  mergeAlertToCase,
} from "pages/dashboard/cases/service";
import { AuthContext } from "context/AuthContext";
import { showToast } from "ui/components/common";
import { CaseInfoType } from "lib/types";
import CaseItem from "ui/cases/merge-case-item";
import { MagnifyingGlassIcon } from "@heroicons/react/24/outline";
import { useDebouncedCallback } from "use-debounce";
import PubSub from "pubsub-js";
import "./alert-merge-case-modal.scss";

const AlertMergeCaseModal = (props: {
  alertId: string;
  isModalOpen: boolean;
  setIsModalOpen: (open: boolean) => void;
  onMerged?: () => void;
}) => {
  const { isModalOpen, setIsModalOpen, alertId, onMerged } = props;

  const { user, privileges } = useContext(AuthContext);
  const [cases, setCases] = useState<CaseInfoType[]>([]);

  const [isCaseLoading, setIsCaseLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [checkedIndex, setCheckedIndex] = useState(-1);
  const [last, setLast] = useState("");
  const [hasNext, setHasNext] = useState(false);

  const isCaseDealer = privileges.includes("caseDealer");

  useEffect(() => {
    if (!alertId || !isModalOpen) {
      return;
    }
    getCaseList(user?.orgId, alertId, "");
  }, [alertId, isModalOpen]);

  const getCaseList = async (
    orgId: string,
    alertId: string,
    displayId: string,
  ) => {
    setIsCaseLoading(true);

    const res = await getMergeableCaseList(
      orgId,
      alertId,
      displayId,
      isCaseDealer,
      false,
    );
    setIsCaseLoading(false);
    if (res.status !== 200) {
      showToast(1, "Failed to get mergeable case list");
      return;
    }

    const data = res?.data?.data as CaseInfoType[];
    setCases(data?.slice(0, Math.min(data?.length, 3)));
    setHasNext(false);
    setLast("");
    setCheckedIndex(res?.data?.length > 0 ? 0 : -1);
    console.log("getMergeableCaseList", res);
  };

  const handleOk = async () => {
    setIsSubmitting(true);
    const caseId = cases[checkedIndex]?.id;

    const res = await mergeAlertToCase(user?.orgId, caseId, alertId);
    setIsSubmitting(false);
    if (res?.status != 200) {
      showToast(1, "Failed to merge into case. Please try again.");
      return;
    }
    onMerged?.();
    setIsModalOpen(false);
    PubSub.publish("nextAlertClicked");
    showToast(0, "The alert has been successfully merged into the case!");
  };

  const handleCancel = () => {
    setIsModalOpen(false);
  };

  const handleInputChanged = useDebouncedCallback((text) => {
    getCaseList(user?.orgId, alertId, text);
  }, 300);

  // useEffect(() => {
  //   const observer = new IntersectionObserver(
  //     function (entries) {
  //       entries.forEach(function (entry) {
  //         if (entry.isIntersecting && hasNext) {
  //           getCaseList(user?.orgId, alertId, "", last);
  //         }
  //       });
  //     },
  //     { threshold: 0.1 },
  //   );
  //   const targetElement = document.getElementById("needLoadMore");

  //   targetElement && observer.observe(targetElement);

  //   return () => {
  //     targetElement && observer.unobserve(targetElement);
  //   };
  // }, [cases]);

  return (
    <>
      <Modal
        className="merge-case-modal"
        centered
        closable={false}
        title={<p className="text-xl pb-6 pt-3">Merge Alert</p>}
        open={isModalOpen}
        footer={
          <div className="mt-6 mb-2">
            <button
              className="rounded-2xl bg-white px-6 py-4 border border-blue-500 hover:border-blue-400 text-sm  transition-colors text-blue-500 hover:text-blue-400 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-500 active:bg-blue-600 aria-disabled:cursor-not-allowed aria-disabled:opacity-50"
              onClick={handleCancel}
            >
              Cancel
            </button>
            <button
              disabled={isSubmitting || checkedIndex === -1}
              className="ml-4 rounded-2xl px-6 py-4 bg-blue-500 text-sm font-medium text-white transition-colors hover:bg-blue-400 disabled:hover:bg-blue-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-500 active:bg-blue-600 aria-disabled:cursor-not-allowed aria-disabled:opacity-50 disabled:opacity-50"
              onClick={handleOk}
            >
              Confirm
            </button>
          </div>
        }
      >
        <p className="font-semibold pb-4">Select a case to merge into:</p>
        <Input
          placeholder="Search Case ID"
          className="h-[54px] rounded-2xl"
          onChange={(e) => {
            handleInputChanged(e.target.value);
          }}
          prefix={
            <MagnifyingGlassIcon className="w-4 text-gray-500 peer-focus:text-gray-900" />
          }
        />
        {cases.map((c, index) => (
          <CaseItem
            key={c?.displayId}
            dataCase={c}
            checked={index === checkedIndex}
            index={index}
            setCheckedCaseIndex={(i) => setCheckedIndex(i)}
          />
        ))}

        <div
          className="text-sm text-center text-neutral-500 h-0.5"
          id="needLoadMore"
        />
        {isCaseLoading && (
          <div className="text-sm text-center text-neutral-500 pb-3 pt-1">
            Loading...
          </div>
        )}
        {!isCaseLoading && !hasNext && cases?.length == 0 && (
          <p>No More Case</p>
        )}
      </Modal>
    </>
  );
};

export default AlertMergeCaseModal;
